/** The Parameters step — every applicable item's editable config values on one
 *  screen, so ops can parameterize the whole checklist in one sitting.
 *
 *  Items needing REQUIRED input come first (they gate the setup run); tunable
 *  ones follow under "defaults apply". Each item is a `SetupItemPanel`, the same
 *  host the Access and Notifications steps use — so the one screen built for
 *  filling inputs also carries Preview, Run and the run log, rather than making
 *  an operator who just filled a field navigate elsewhere to use it. Each panel
 *  saves on its own, so a failure isolates to its item, and the form can stay
 *  partially filled: nothing here blocks navigation.
 */
import { Alert, Box, Divider, Stack, Typography } from '@mui/material';

import type { PreflightItem } from '@/api/preflight';

import { PreflightPreviewDialog } from '../../tabs/PreflightRunLog';
import type { PreflightItemActions } from '../../tabs/usePreflightItemActions';
import { SetupItemPanel } from '../SetupItemPanel';

interface Props {
  items: PreflightItem[];
  topId: number;
  toCode: string;
  canManage: boolean;
  actions: PreflightItemActions;
}

export function ParametersStep({ items, topId, toCode, canManage, actions }: Props) {
  if (items.length === 0) {
    return <Alert severity="info">No items take input here — everything runs with defaults.</Alert>;
  }
  // "Required to run" = the run cannot proceed without you — NOT "has a field
  // marked required". `RESTECH_SOURCE_CONFIG`'s `source_id` is required and
  // already satisfied by the booking key every tenant is created with, so keying
  // off the field flag listed a configured item as outstanding work.
  const required = items.filter(
    (i) => i.input_kind === 'required' || (i.status !== 'completed' && !i.ready),
  );
  const optional = items.filter((i) => !required.includes(i));
  const unfilled = items.filter((i) => i.status !== 'completed' && !i.ready);

  const panels = (list: PreflightItem[]) => (
    <Stack spacing={2}>
      {list.map((item) => (
        <SetupItemPanel
          key={item.id}
          item={item}
          topId={topId}
          toCode={toCode}
          canManage={canManage}
          actions={actions}
        />
      ))}
    </Stack>
  );

  return (
    <Stack spacing={3}>
      <Alert severity={unfilled.length ? 'info' : 'success'}>
        {unfilled.length
          ? `${unfilled.length} item(s) still need input before they can run: ` +
            `${unfilled.map((i) => i.title).join(', ')}. ` +
            'This form can stay partially filled — items without input run with defaults or wait on the checklist.'
          : 'Every item has its required input — the setup run can take it from here.'}
      </Alert>

      {required.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Required to run
          </Typography>
          {panels(required)}
        </Box>
      )}

      {required.length > 0 && optional.length > 0 && <Divider />}

      {optional.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Optional — defaults apply
          </Typography>
          {panels(optional)}
        </Box>
      )}

      {/* Rendered once for the step: `actions` (and its previewRun) is shared. */}
      <PreflightPreviewDialog run={actions.previewRun} onClose={actions.clearPreview} />
    </Stack>
  );
}
